import { useRef, useState } from "react";
import { usePage } from "@inertiajs/react";
import { CheckIcon } from "@heroicons/react/24/solid";
import { GoogleOAuthButton } from "@components/auth/GoogleOAuthButton";
import { TurnstileWidget, type TurnstileWidgetHandle } from "@components/auth/TurnstileWidget";
import { DeployPanel } from "@components/deploy/DeployPanel";

const ACCESS_ITEMS = [
  "Your email address",
  "Google Ads Account Management",
  "Campaign creating and editing",
];

/** Google "G" logo */
function GoogleLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

// Only the turnstile key is needed — read it from the page props by name.
type TurnstilePropsSlice = {
  turnstile_site_key?: string | null;
  [key: string]: unknown;
};

export default function GoogleConnectScreen({ actions }: { actions?: React.ReactNode }) {
  const { turnstile_site_key } = usePage<TurnstilePropsSlice>().props;
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  const [turnstileReady, setTurnstileReady] = useState(false);

  const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";
  const oauthPath = `/users/auth/google_oauth2?redirect_to=${encodeURIComponent(window.location.pathname + window.location.search)}`;

  const turnstileRequired = !!turnstile_site_key;
  const blockedByTurnstile = turnstileRequired && !turnstileReady;

  return (
    <DeployPanel.Root>
      <DeployPanel.Header>
        <DeployPanel.Title
          description="Google Ads requires an account with Google. Connect here so your ads run and results track. You stay in control and own the accounts."
        >
          Connect your Google Account
        </DeployPanel.Title>
        {actions && <DeployPanel.Actions>{actions}</DeployPanel.Actions>}
      </DeployPanel.Header>

      <DeployPanel.Body centered>
        <div className="mx-auto w-full max-w-[506px]">
          {/* What we'll access box */}
          <div className="w-full border border-neutral-200 rounded-lg bg-neutral-50 flex flex-col items-center justify-center gap-6 px-6 py-5 min-h-[258px]">
            <div className="flex flex-col items-center gap-3">
              <GoogleLogo className="size-8" />
              <h3 className="text-lg font-semibold text-base-500">What we&apos;ll access</h3>
            </div>
            <ul className="flex flex-col gap-3">
              {ACCESS_ITEMS.map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckIcon className="size-4 text-success-500 shrink-0" />
                  <span className="text-base text-base-600">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Sign in button */}
          <GoogleOAuthButton
            csrfToken={csrfToken}
            oauthPath={oauthPath}
            label={blockedByTurnstile ? "Verifying…" : "Sign in with Google"}
            getTurnstileToken={() => turnstileRef.current?.getToken()}
            disabled={blockedByTurnstile}
            className="mt-5"
          />

          {turnstile_site_key && (
            <TurnstileWidget
              ref={turnstileRef}
              siteKey={turnstile_site_key}
              onReady={setTurnstileReady}
            />
          )}
        </div>
      </DeployPanel.Body>
    </DeployPanel.Root>
  );
}
